import type { Accent, AccentColor } from "@ui/types/accent";

/**
 * Brand-accent → Tailwind class maps for the roles Figma's Accent collection
 * has no rung for. Each record below is one visual role; its keys are the
 * accents that role actually supports. Roles use deliberately different shades
 * (a progress segment is solid, a selected label is deep), so consumers pick a
 * role — never mix shades across roles. Values are full literal class strings
 * so Tailwind's scanner picks them up.
 *
 * A role the collection *does* cover is spelled as a semantic utility under
 * `accentScope(accent)` instead — see `css/accent.css`.
 */

/** The extended accent set available on nav surfaces (`TopBarNavLink`). */
export type NavAccentColor = Extract<Accent, "blue" | "green" | "orange" | "pink" | "purple" | "yellow">;

/** Solid progress segment / bar fill (`TopBarProgress`, `FileDropField`). */
export const accentSegment: Record<AccentColor, string> = {
  green: "bg-green-500",
  purple: "bg-purple-500",
  red: "bg-red-400",
  yellow: "bg-yellow-500",
};

/**
 * Accent palette for the tile family: the four core brand accents plus
 * ToggleTile's extras (pink for hearts, neutral for uncoloured chips).
 */
export type TileFamilyAccent = AccentColor | "pink" | "neutral";

/**
 * Selected label/caption text per accent — identical across Tile (flat
 * selected label), ToggleTile (horizontal selected caption) and
 * RadioGroupWithFollowUp (inline follow-up label). yellow uses yellow-900
 * (not the green-800 analog yellow-800) to hold contrast on the light
 * yellow-50 fill; neutral keeps the resting text colour.
 */
export const accentSelectedText: Record<TileFamilyAccent, string> = {
  red: "text-red-600",
  green: "text-green-800",
  purple: "text-purple-700",
  yellow: "text-yellow-900",
  pink: "text-pink-600",
  neutral: "text-neutral-900",
};
